import { AllAgenciesOrRealtorsOrCompaniesType, UserResponse } from "../../types/user/user.model";
import { api } from "../axios";
import { cleanProfileText } from "../../helpers/profileText";

const GET_PROFILE_URL = import.meta.env.VITE_GET_PROFILE_URL;
const UPDATE_USER_URL = import.meta.env.VITE_UPDATE_PROFILE_URL;
const GET_ALL_USERS_URL = import.meta.env.VITE_GET_ALL_USERS_URL;
const GET_ALL_REALTORS_URL = import.meta.env.VITE_GET_ALL_REALTORS_URL;
const GET_REALTORS_BY_COMPANY_OR_AGENCY_ID_URL = import.meta.env.VITE_GET_REALTORS_BY_COMPANY_OR_AGENCY_ID_URL;
const ASSIGN_REALTOR_TO_COMPANY_OR_AGENCY_URL = import.meta.env.VITE_ASSIGN_REALTOR_TO_COMPANY_OR_AGENCY_URL;
const UNASSIGN_REALTOR_FROM_COMPANY_OR_AGENCY_URL = import.meta.env.VITE_UNASSIGN_REALTOR_FROM_COMPANY_OR_AGENCY_URL;
const DELETE_ACCOUNT_URL = import.meta.env.VITE_DELETE_ACCOUNT_URL;
const GET_ALL_AGENCIES_OR_COMPANIES_OR_REALTORS_URL = import.meta.env.VITE_GET_ALL_AGENCIES_OR_COMPANIES_OR_REALTORS_URL;


export const getUserProfile = async (userId: number): Promise<UserResponse> => {
  try {
    const res = await api.get(GET_PROFILE_URL + userId);
    return cleanProfileText(res.data);
  } catch (error) {
    console.log("getUserProfile in userApi error: ", error);
    throw error;
  }
};

export const updateUser = async ({
  userId,
  userRequest,
}: {
  userId: number;
  userRequest: UserResponse;
}) => {
  try {
    const res = await api.post(UPDATE_USER_URL, cleanProfileText(userRequest));
    return res.data;
  } catch (error) {
    console.log("updateUser in userApi error: ", error);
    throw error;
  }
};

export const getAllUsersApi = async (): Promise<UserResponse[]> => {
  try {
    const res = await api.get(GET_ALL_USERS_URL);
    return res.data;
  } catch (error) {
    console.log("getAllUsersApi in userApi error: ", error);
    throw error;
  }
};

export const getAllRealtorsApi = async (): Promise<UserResponse[]> => {
  try {
    // The endpoint is paginated and defaults to 10 items — ask for a large page
    // so the directory shows every realtor, not just the first screen.
    const res = await api.get(GET_ALL_REALTORS_URL, {
      params: { page: 1, offset: 1000 },
    });
    return (res.data?.list ?? []).map(cleanProfileText);
  } catch (error) {
    console.log("getAllRealtorsApi in userApi error: ", error);
    throw error;
  }
};

export const assignAgentsToCompanyOrAgencyApi = async (agentsIds: number[]) => {
  try {
    const res = await api.post(ASSIGN_REALTOR_TO_COMPANY_OR_AGENCY_URL, {
      "user_ids": agentsIds
    });
    return res.data;
  } catch (error) {
    console.log("getAllRealtorsApi in userApi error: ", error);
    throw error;
  }
}

export const unassignAgentFromCompanyOrAgencyApi = async (agentId: number) => {
  try {
    const res = await api.post(UNASSIGN_REALTOR_FROM_COMPANY_OR_AGENCY_URL, {
      'user_ids': [agentId]
    });
    return res.data;
  } catch (error) {
    console.log("getAllRealtorsApi in userApi error: ", error);
    throw error;
  }
}

export const deleteAccount = async (): Promise<void> => {
  try {
    await api.delete(DELETE_ACCOUNT_URL);
  } catch (error) {
    console.log("deleteAccount in userApi error: ", error);
    throw error;
  }
};

export const getRealtorsByCompanyOrAgencyIdApi = async (companyOrAgencyId: number, page: number, offset: number): Promise<UserResponse[]> => {
  try {
    const res = await api.get(`${GET_REALTORS_BY_COMPANY_OR_AGENCY_ID_URL}${companyOrAgencyId}/realtors?page=${page}&offset=${offset}`);
    return (res.data?.list || []).map(cleanProfileText);
  } catch (error) {
    console.log("getRealtorsByCompanyOrAgencyIdApi in userApi error: ", error);
    throw error;
  }
}

function serializeSpecialistParams(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      value.forEach((v) => sp.append(key, String(v)));
    } else {
      sp.append(key, String(value));
    }
  });
  return sp.toString();
}

export const getAllAgenciesOrRealtorsOrCompaniesApi = async (
  allAgenciesOrRealtorsOrCompanies: AllAgenciesOrRealtorsOrCompaniesType,
): Promise<{ list: UserResponse[]; total: number }> => {
  try {
    const res = await api.get(GET_ALL_AGENCIES_OR_COMPANIES_OR_REALTORS_URL, {
      params: allAgenciesOrRealtorsOrCompanies,
      paramsSerializer: { serialize: serializeSpecialistParams }
    });
    return { list: (res.data?.list || []).map(cleanProfileText), total: res.data?.total || 0 };
  } catch (error) {
    console.log(`getAll${allAgenciesOrRealtorsOrCompanies.type}Api in userApi error: `, error);
    throw error;
  }
};